import React, { useEffect, useMemo, useState } from 'react';
import { leaveAPI, workRequestAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const ApprovalsInboxModule = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const pendingItems = useMemo(
    () => items.filter((x) => x.status === 'pending' || x.status === 'pending_level2'),
    [items]
  );

  const loadInbox = async () => {
    try {
      setLoading(true);
      const [leavesRes, regRes, wfhRes, outRes] = await Promise.all([
        leaveAPI.list({ status: 'pending' }).catch(() => ({ data: [] })),
        workRequestAPI.getTeamRegularizations({ status: 'pending' }).catch(() => ({ data: [] })),
        workRequestAPI.getTeamWfh({ status: 'pending' }).catch(() => ({ data: [] })),
        workRequestAPI.getTeamOutstation({ status: 'pending' }).catch(() => ({ data: [] })),
      ]);

      const leaveItems = (leavesRes.data || []).map((r) => ({
        id: `leave-${r.id}`,
        kind: 'leave',
        requestId: r.id,
        title: 'Leave Request',
        subtitle: `${r.employee_name} • ${r.leave_type}`,
        detail: `${r.start_date} to ${r.end_date}`,
        status: r.status,
      }));
      const regItems = (regRes.data || []).map((r) => ({
        id: `regularization-${r.id}`,
        kind: 'regularization',
        requestId: r.id,
        title: 'Regularization',
        subtitle: `${r.employee_name}`,
        detail: r.attendance_date || '-',
        status: r.status,
      }));
      const wfhItems = (wfhRes.data || []).map((r) => ({
        id: `wfh-${r.id}`,
        kind: 'wfh',
        requestId: r.id,
        title: 'WFH Request',
        subtitle: `${r.employee_name}`,
        detail: `${r.from_date} to ${r.to_date}`,
        status: r.status,
      }));
      const outItems = (outRes.data || []).map((r) => ({
        id: `outstation-${r.id}`,
        kind: 'outstation',
        requestId: r.id,
        title: 'Outstation Duty',
        subtitle: `${r.employee_name}`,
        detail: `${r.from_date} to ${r.to_date}`,
        status: r.status,
      }));

      setItems([...leaveItems, ...regItems, ...wfhItems, ...outItems]);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load approvals inbox'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInbox();
  }, []);

  const handleApprove = async (item) => {
    try {
      if (item.kind === 'leave') {
        if (item.status === 'pending_level2') {
          await leaveAPI.approveLevel2(item.requestId, '');
        } else {
          await leaveAPI.approveLevel1(item.requestId, '');
        }
      } else if (item.kind === 'regularization') {
        await workRequestAPI.approveRegularization(item.requestId, {});
      } else if (item.kind === 'wfh') {
        await workRequestAPI.approveWfh(item.requestId, {});
      } else {
        await workRequestAPI.approveOutstation(item.requestId, {});
      }
      toast.success('Request approved');
      await loadInbox();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve request'));
    }
  };

  const handleReject = async (item) => {
    const reason = window.prompt('Enter rejection reason');
    if (!reason) return;
    try {
      if (item.kind === 'leave') {
        await leaveAPI.reject(item.requestId, reason);
      } else if (item.kind === 'regularization') {
        await workRequestAPI.rejectRegularization(item.requestId, { rejection_reason: reason });
      } else if (item.kind === 'wfh') {
        await workRequestAPI.rejectWfh(item.requestId, { rejection_reason: reason });
      } else {
        await workRequestAPI.rejectOutstation(item.requestId, { rejection_reason: reason });
      }
      toast.success('Request rejected');
      await loadInbox();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reject request'));
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Approvals Inbox</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{pendingItems.length} pending</Badge>
            {loading && <span className="text-sm text-muted-foreground">Refreshing...</span>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Unified Queue</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {pendingItems.map((item) => (
            <div key={item.id} className="border rounded-md p-3 flex items-start justify-between">
              <div>
                <p className="font-medium">{item.title}</p>
                <p className="text-sm text-muted-foreground">{item.subtitle}</p>
                <p className="text-sm text-muted-foreground">{item.detail}</p>
                <Badge variant="warning" className="mt-2">{item.status}</Badge>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handleApprove(item)}>Approve</Button>
                <Button size="sm" variant="destructive" onClick={() => handleReject(item)}>Reject</Button>
              </div>
            </div>
          ))}
          {pendingItems.length === 0 && (
            <p className="text-sm text-muted-foreground">No pending approvals.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ApprovalsInboxModule;

